// Replay transport — separate from the main event queue so that high-volume
// $snapshot chunks don't compete for the analytics HTTP slot.
//
// Behavior:
// - `send(chunk)` POSTs the chunk to `/replays` (configurable). Returns a
//   promise that resolves on 2xx and rejects otherwise; callers treat the
//   transport as best-effort and never block recording on its result.
// - `flushBeacon()` is the page-hide path: it serializes any pending
//   in-flight chunks and posts via `navigator.sendBeacon`. Fire-and-forget.
// - Internal in-flight buffer is bounded (200 chunks / 10 MB approx) — when
//   over the limit we drop the OLDEST chunks rather than crashing the page.
import type { SnapshotChunk } from './types.js';

/** Soft cap on in-flight chunks awaiting upload. */
const MAX_INFLIGHT_CHUNKS = 200;
/** Soft cap on aggregate bytes of in-flight chunks. */
const MAX_INFLIGHT_BYTES = 10 * 1024 * 1024;

export interface ReplayTransportOptions {
  /** Full URL to POST chunks to (e.g. `https://in.layers.com/replays`). */
  endpoint: string;
  /** App ID — included in body and `X-App-Id` header. */
  appId: string;
  /** SDK version for the `X-SDK-Version` header. */
  sdkVersion: string;
  /** Forwarded for debug logging. */
  enableDebug?: boolean;
}

export class ReplayTransport {
  private readonly endpoint: string;
  private readonly appId: string;
  private readonly sdkVersion: string;
  private readonly enableDebug: boolean;

  /** Chunks waiting to be (re)sent. Kept in arrival order. */
  private inflight: SnapshotChunk[] = [];
  private inflightBytes = 0;

  constructor(options: ReplayTransportOptions) {
    this.endpoint = options.endpoint;
    this.appId = options.appId;
    this.sdkVersion = options.sdkVersion;
    this.enableDebug = options.enableDebug ?? false;
  }

  /**
   * Try to deliver a chunk now. Resolves once the POST completes.
   * Failures are logged but never thrown to the caller — replay is
   * always best-effort.
   */
  async send(chunk: SnapshotChunk): Promise<void> {
    this.enqueue(chunk);

    // Kick off send for THIS chunk specifically. We don't drain the whole
    // inflight buffer here because the caller (Recorder) only ever sends
    // one chunk at a time; this keeps the call site simple.
    try {
      const ok = await this.postChunk(chunk);
      if (ok) {
        this.dequeue(chunk);
      }
    } catch (e) {
      if (this.enableDebug) {
        console.warn('[Layers/replay] transport.send failed:', e);
      }
    }
  }

  /**
   * Page-hide path: synchronously emit any in-flight chunks via
   * `navigator.sendBeacon`. Best-effort fire-and-forget — `sendBeacon`
   * cannot report success.
   */
  flushBeacon(): void {
    if (this.inflight.length === 0) return;
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;

    // Send each chunk as its own beacon — keeps payloads under the
    // 64 KB sendBeacon limit on most browsers.
    const remaining: SnapshotChunk[] = [];
    let remainingBytes = 0;
    for (const chunk of this.inflight) {
      const body = this.serialize(chunk);
      try {
        const ok = navigator.sendBeacon(this.endpoint, body);
        if (!ok) {
          remaining.push(chunk);
          remainingBytes += chunk.size_bytes;
        }
      } catch {
        remaining.push(chunk);
        remainingBytes += chunk.size_bytes;
      }
    }
    this.inflight = remaining;
    this.inflightBytes = remainingBytes;
  }

  /** Discard everything in the in-flight buffer (e.g. on shutdown). */
  reset(): void {
    this.inflight = [];
    this.inflightBytes = 0;
  }

  /** For tests/diagnostics. */
  getInflightCount(): number {
    return this.inflight.length;
  }

  // --- internals -------------------------------------------------------

  private enqueue(chunk: SnapshotChunk): void {
    this.inflight.push(chunk);
    this.inflightBytes += chunk.size_bytes;

    // Drop oldest when over either soft cap.
    while (this.inflight.length > MAX_INFLIGHT_CHUNKS || this.inflightBytes > MAX_INFLIGHT_BYTES) {
      const dropped = this.inflight.shift();
      if (!dropped) break;
      this.inflightBytes -= dropped.size_bytes;
      if (this.enableDebug) {
        console.warn(
          `[Layers/replay] dropped chunk ${String(dropped.chunk_index)} ` +
            `(buffer over cap: ${String(this.inflight.length + 1)}/${String(MAX_INFLIGHT_CHUNKS)} ` +
            `chunks, ~${String(this.inflightBytes + dropped.size_bytes)}/${String(MAX_INFLIGHT_BYTES)} B)`
        );
      }
    }
  }

  private dequeue(chunk: SnapshotChunk): void {
    const idx = this.inflight.indexOf(chunk);
    if (idx >= 0) {
      this.inflight.splice(idx, 1);
      this.inflightBytes -= chunk.size_bytes;
      if (this.inflightBytes < 0) this.inflightBytes = 0;
    }
  }

  private async postChunk(chunk: SnapshotChunk): Promise<boolean> {
    if (typeof fetch === 'undefined') return false;
    const body = this.serialize(chunk);
    try {
      const res = await fetch(this.endpoint, {
        method: 'POST',
        // Mirror the events endpoint shape — replay is auth-by-app-id.
        headers: {
          'Content-Type': 'application/json',
          'X-App-Id': this.appId,
          'X-SDK-Version': this.sdkVersion,
          'X-Replay': 'true'
        },
        body,
        // Replay POSTs should not block page navigation.
        keepalive: true
      });
      return res.ok;
    } catch (e) {
      if (this.enableDebug) {
        console.warn('[Layers/replay] postChunk fetch threw:', e);
      }
      return false;
    }
  }

  private serialize(chunk: SnapshotChunk): string {
    return JSON.stringify({
      app_id: this.appId,
      replay: true,
      chunk: chunk
    });
  }
}
